import { deleteUserEntityPermissions } from 'config/requests/userEntityPermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { UserEntityPermissionFormValues } from 'shared/widgets/UserEntityPermissionsDialog/UserEntityPermissionsDialogCreate/UserEntityPermissionsDialogCreate';
import {
  UserEntityPermission,
  UserEntityPermissionsNames,
} from 'types/embedded/userEntityPermission';

type UseUserEntityPermissionsFieldArrayReturnValues = {
  fields: UserEntityPermission[];
  onAddField: () => void;
  onRemoveField: (index: number) => Promise<any>;
};

export function useUserEntityPermissionsFieldArray(
  recordId: string,
  entityName: UserEntityPermissionsNames,
): UseUserEntityPermissionsFieldArrayReturnValues {
  const translate = useTypedTranslate();

  const { control } = useFormContext<UserEntityPermissionFormValues>();
  const notify = useNotify();

  const { fields, append, remove } = useFieldArray({
    name: 'permissions',
    control,
    keyName: '_key',
  });

  const onAddField = (): void => {
    append({
      entity: entityName,
      rowId: Number(recordId),
      user: null,
      expiredAt: null,
    });
  };

  const onRemoveField = async (index: number): Promise<any> => {
    const permissionId = fields[index].id;
    if (permissionId) {
      try {
        await deleteUserEntityPermissions(permissionId);
        await queryClient.refetchQueries({ queryKey: ['user_entity_permissions'] });
        notify(translate('app.merchantsShowNotifications.accessSettingsDeletedSuccessfully'), {
          type: 'success',
        });
      } catch (error) {
        notify(
          `${translate('app.common.error')}: ${error as string}` ||
            translate('app.merchantsShowNotifications.accessSettingsDeletionError'),
          {
            type: 'error',
          },
        );
      }
      return;
    }
    remove(index);
  };

  return {
    fields,
    onAddField,
    onRemoveField,
  };
}
